import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  fetchWaitlistPlaceholder,
  joinWaitlist,
  type WaitlistEntry,
  type WaitlistJoinInput,
  type WaitlistLanguage,
  type WaitlistPlaceholder,
} from '@/lib/api/waitlist';
import { ApiError } from '@/lib/api/client';

/**
 * useWaitlist — TanStack Query hooks for the two backend waitlist endpoints.
 *
 * `useWaitlistPlaceholder()` — fetches the current A/B-variant placeholder
 * string and variant code for the active i18n language. `staleTime: Infinity`
 * because variant assignment is sticky for the session and the placeholder
 * copy is stable; refetching only happens on language change (different key).
 * One retry — the placeholder is non-critical UI, the form falls back to the
 * i18n string if the query never resolves.
 *
 * `useJoinWaitlist()` — POSTs the form payload. Backend is idempotent on
 * email so re-submits aren't an error. Returned `WaitlistEntry` (`{ id,
 * email, created_at }`) is currently unused by the UI — we just need the
 * 2xx to flip the success state — but it's typed in case a follow-up wants
 * to surface "we already had your email since 2026-04-01" via `created_at`.
 */

function langCode(i18nLanguage: string): WaitlistLanguage {
  return i18nLanguage.startsWith('en') ? 'en' : 'mk';
}

export function useWaitlistPlaceholder() {
  const { i18n } = useTranslation();
  const lang = langCode(i18n.language);
  return useQuery<WaitlistPlaceholder, ApiError>({
    queryKey: ['waitlist', 'placeholder', lang],
    queryFn: () => fetchWaitlistPlaceholder(lang),
    staleTime: Infinity,
    retry: 1,
  });
}

export function useJoinWaitlist() {
  return useMutation<WaitlistEntry, ApiError, WaitlistJoinInput>({
    mutationFn: (input) => joinWaitlist(input),
  });
}

export { langCode as toWaitlistLanguage };
